import { useCallback } from 'react'
import { Checkbox, FormControlLabel, InputLabel, Stack, Typography } from '@mui/material'
import useId from '@mui/utils/useId'
import { Circle as CircleIcon } from '@mui/icons-material'
import { type CartStatus } from '../../UserData/_stores/UserCartStore'
import useOrderStatusProps from '../../Common/_hooks/Order/useOrderStatusProps'

type Props = {
  label: string
  value: CartStatus[]
  statuses: CartStatus[]
  onChange: (value: CartStatus[]) => void
}

function OrderStatusFilterItem(props: {
  checked: boolean
  disabled?: boolean
  onChange: (status: CartStatus, checked: boolean) => void
  status: CartStatus
}) {
  const { text, color } = useOrderStatusProps(props.status)

  return <FormControlLabel
    control={<Checkbox
      size="small"
      checked={props.checked}
      disabled={props.disabled}
      onChange={(e, checked) => props.onChange(props.status, checked)}
      sx={{
        p: 0.75,
        mr: 0.5,
      }}
    />}
    label={<Stack
      direction="row"
      spacing={1}
      alignItems="center"
      justifyContent="space-between"
      sx={{
        minWidth: 0,
        width: '100%',
      }}
    >
      <Typography
        variant="body2"
        sx={{
          minWidth: 0,
        }}
      >{text}</Typography>
      <CircleIcon
        color={color}
        sx={{
          flexShrink: 0,
          fontSize: 10,
        }}
      />
    </Stack>}
    disableTypography
    sx={{
      alignItems: 'center',
      minHeight: 34,
      m: 0,
      width: '100%',
      '.MuiFormControlLabel-label': {
        flex: 1,
        minWidth: 0,
      },
    }}
  />
}

export default function OrderStatusFilter(props: Props) {
  const labelId = useId()

  const onStatusChange = useCallback((status: CartStatus, checked: boolean) => {
    if (checked) {
      props.onChange(props.value.includes(status) ? props.value : [...props.value, status])
      return
    }

    if (props.value.length <= 1) {
      return
    }

    props.onChange(props.value.filter(value => value !== status))
  }, [props.onChange, props.value])

  return <Stack spacing={1}>
    <InputLabel id={labelId}>{props.label}</InputLabel>

    <Stack spacing={0.5} role="group" aria-labelledby={labelId}>
      {props.statuses.map(status => (
        <OrderStatusFilterItem
          key={status}
          status={status}
          checked={props.value.includes(status)}
          disabled={props.value.length <= 1 && props.value.includes(status)}
          onChange={onStatusChange}
        />
      ))}
    </Stack>
  </Stack>
}
